// =====================================================
// GEOFENCE TYPE DEFINITIONS FOR PRODUCTION SECURITY
// =====================================================

export type GeofenceType = 'circle' | 'square' | 'triangle' | 'polygon' | 'point'

export interface GeofenceCoordinate {
  lat: number
  lng: number
}

export interface GeofenceBounds {
  lat: number
  lng: number
  sequence_order: number
}

// Client-side geofence payload (what we send to API)
export interface GeofencePayload {
  name: string
  description?: string
  type: GeofenceType
  center?: GeofenceCoordinate
  radius?: number // For circles (in meters)
  bounds?: GeofenceBounds[] // For polygons/complex shapes
  color: string
  fillColor: string
}

// Server response geofence (what we get from API)
export interface GeofenceResponse extends GeofencePayload {
  id: string
  is_active: boolean
  alert_on_entry: boolean
  alert_on_exit: boolean
  created_at: string
  updated_at: string
  created_by: string
}

// Validation constraints
export const GEOFENCE_CONSTRAINTS = {
  MIN_RADIUS: 10, // meters
  MAX_RADIUS: 50000, // 50km
  MIN_POLYGON_VERTICES: 3,
  MAX_POLYGON_VERTICES: 100,
  MAX_AREA: 1000000000, // square meters (1000 km²)
  MAX_NAME_LENGTH: 255,
  MAX_DESCRIPTION_LENGTH: 1000
} as const

// Type guards for runtime type checking
export const isCircleGeofence = (geofence: GeofenceResponse): geofence is GeofenceResponse & { type: 'circle'; radius: number } => {
  return geofence.type === 'circle' && typeof geofence.radius === 'number'
}

export const isPolygonGeofence = (geofence: GeofenceResponse): geofence is GeofenceResponse & { type: 'polygon'; bounds: GeofenceBounds[] } => {
  return geofence.type === 'polygon' && Array.isArray(geofence.bounds)
}

export const isPointGeofence = (geofence: GeofenceResponse): geofence is GeofenceResponse & { type: 'point'; center: GeofenceCoordinate } => {
  return geofence.type === 'point' && geofence.center !== undefined
}

// Validation functions
export const validateGeofencePayload = (payload: Partial<GeofencePayload>): { isValid: boolean; errors: string[] } => {
  const errors: string[] = []

  // Required fields
  if (!payload.name?.trim()) {
    errors.push('Name is required')
  } else if (payload.name.length > GEOFENCE_CONSTRAINTS.MAX_NAME_LENGTH) {
    errors.push(`Name must be less than ${GEOFENCE_CONSTRAINTS.MAX_NAME_LENGTH} characters`)
  }

  if (!payload.type) {
    errors.push('Geofence type is required')
  }

  if (!payload.color) {
    errors.push('Color is required')
  }

  // Type-specific validation
  if (payload.type === 'circle') {
    if (!payload.radius) {
      errors.push('Radius is required for circle geofences')
    } else if (payload.radius < GEOFENCE_CONSTRAINTS.MIN_RADIUS) {
      errors.push(`Radius must be at least ${GEOFENCE_CONSTRAINTS.MIN_RADIUS} meters`)
    } else if (payload.radius > GEOFENCE_CONSTRAINTS.MAX_RADIUS) {
      errors.push(`Radius must be less than ${GEOFENCE_CONSTRAINTS.MAX_RADIUS} meters`)
    }

    if (!payload.center) {
      errors.push('Center coordinates are required for circle geofences')
    }
  }

  if (payload.type === 'polygon') {
    if (!payload.bounds || !Array.isArray(payload.bounds)) {
      errors.push('Bounds are required for polygon geofences')
    } else if (payload.bounds.length < GEOFENCE_CONSTRAINTS.MIN_POLYGON_VERTICES) {
      errors.push(`Polygon must have at least ${GEOFENCE_CONSTRAINTS.MIN_POLYGON_VERTICES} vertices`)
    } else if (payload.bounds.length > GEOFENCE_CONSTRAINTS.MAX_POLYGON_VERTICES) {
      errors.push(`Polygon must have less than ${GEOFENCE_CONSTRAINTS.MAX_POLYGON_VERTICES} vertices`)
    }
  }

  if (payload.type === 'point') {
    if (!payload.center) {
      errors.push('Center coordinates are required for point geofences')
    }
  }

  // Coordinate validation
  if (payload.center) {
    if (typeof payload.center.lat !== 'number' || payload.center.lat < -90 || payload.center.lat > 90) {
      errors.push('Invalid latitude coordinate')
    }
    if (typeof payload.center.lng !== 'number' || payload.center.lng < -180 || payload.center.lng > 180) {
      errors.push('Invalid longitude coordinate')
    }
  }

  if (payload.bounds) {
    payload.bounds.forEach((bound, index) => {
      if (typeof bound.lat !== 'number' || bound.lat < -90 || bound.lat > 90) {
        errors.push(`Invalid latitude at vertex ${index + 1}`)
      }
      if (typeof bound.lng !== 'number' || bound.lng < -180 || bound.lng > 180) {
        errors.push(`Invalid longitude at vertex ${index + 1}`)
      }
    })
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

// Utility function to calculate polygon area (for validation)
export const calculatePolygonArea = (bounds: GeofenceBounds[]): number => {
  if (bounds.length < 3) return 0

  let area = 0
  const n = bounds.length

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    area += bounds[i].lng * bounds[j].lat
    area -= bounds[j].lng * bounds[i].lat
  }

  return Math.abs(area) / 2
}

// Normalize polygon to ensure clockwise winding
export const normalizePolygon = (bounds: GeofenceBounds[]): GeofenceBounds[] => {
  if (bounds.length < 3) return bounds

  // Calculate signed area to determine winding order
  let signedArea = 0
  const n = bounds.length

  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n
    signedArea += (bounds[j].lng - bounds[i].lng) * (bounds[j].lat + bounds[i].lat)
  }

  // If counter-clockwise (positive area), reverse the order
  if (signedArea > 0) {
    return [...bounds].reverse().map((bound, index) => ({
      ...bound,
      sequence_order: index + 1
    }))
  }

  return bounds
}

